import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_CODE_FONT_SIZE,
  DEFAULT_UI_FONT_SIZE,
  MAX_APP_FONT_SIZE,
  MIN_APP_FONT_SIZE,
  type ThemePreference,
} from "@cmmd-forge/contracts/settings";

import { applyAppearanceSurfaces } from "../appearanceSurfaces";
import { hasThemeStorage, useThemeHalves } from "./useThemeHalves";
import { syncBrowserChromeTheme } from "./browserChromeTheme";

import {
  CLIENT_SETTINGS_STORAGE_KEY,
  normalizeClientSettingsInput,
} from "../clientPersistenceStorage";
import { useSettings, useUpdateSettings } from "./useSettings";

export type Theme = ThemePreference;
export type ResolvedTheme = "light" | "dark";

const LEGACY_THEME_STORAGE_KEY = "forge:theme";
const SYSTEM_DARK_QUERY = "(prefers-color-scheme: dark)";

function isTheme(value: unknown): value is Theme {
  return value === "system" || value === "light" || value === "dark";
}

function normalizeFontSize(value: unknown, fallback: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return fallback;
  }
  const roundedValue = Math.round(value);
  return Math.min(MAX_APP_FONT_SIZE, Math.max(MIN_APP_FONT_SIZE, roundedValue));
}

function readInitialStoredAppearance(): {
  readonly theme: Theme;
  readonly uiFontSize: number;
  readonly codeFontSize: number;
} {
  if (!hasThemeStorage()) {
    return {
      theme: "system",
      uiFontSize: DEFAULT_UI_FONT_SIZE,
      codeFontSize: DEFAULT_CODE_FONT_SIZE,
    };
  }

  try {
    const clientSettings = window.localStorage.getItem(CLIENT_SETTINGS_STORAGE_KEY);
    if (clientSettings) {
      // Through the same migration the schema reader uses. This read happens
      // before paint and the schema read happens after hydration; if they
      // disagree on a size, the app visibly resizes a frame in.
      const parsed = normalizeClientSettingsInput(JSON.parse(clientSettings)) as {
        readonly theme?: unknown;
        readonly uiFontSize?: unknown;
        readonly codeFontSize?: unknown;
      };
      return {
        theme: isTheme(parsed.theme) ? parsed.theme : "system",
        uiFontSize: normalizeFontSize(parsed.uiFontSize, DEFAULT_UI_FONT_SIZE),
        codeFontSize: normalizeFontSize(parsed.codeFontSize, DEFAULT_CODE_FONT_SIZE),
      };
    }

    const legacyTheme = window.localStorage.getItem(LEGACY_THEME_STORAGE_KEY);
    return {
      theme: isTheme(legacyTheme) ? legacyTheme : "system",
      uiFontSize: DEFAULT_UI_FONT_SIZE,
      codeFontSize: DEFAULT_CODE_FONT_SIZE,
    };
  } catch {
    return {
      theme: "system",
      uiFontSize: DEFAULT_UI_FONT_SIZE,
      codeFontSize: DEFAULT_CODE_FONT_SIZE,
    };
  }
}

/**
 * The stored mix as written, without resolvability pruning. Rebuilding the
 * mix from the pruned parse would silently erase a half whose theme is not
 * resolvable yet, so writes start from here (upstream's readThemeHalvesRaw).
 */
function getSystemResolvedTheme(): ResolvedTheme {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return "dark";
  }

  return window.matchMedia(SYSTEM_DARK_QUERY).matches ? "dark" : "light";
}

function resolveTheme(theme: Theme): ResolvedTheme {
  return theme === "system" ? getSystemResolvedTheme() : theme;
}

function syncDesktopTheme(theme: Theme): void {
  if (typeof window === "undefined") return;
  const bridge = window.desktopBridge;
  if (!bridge) {
    return;
  }

  void bridge.setTheme(theme).catch(() => {
    // Best-effort only.
  });
}

function applyTheme(theme: Theme, suppressTransitions = false): ResolvedTheme {
  const resolvedTheme = resolveTheme(theme);
  if (typeof document === "undefined" || typeof window === "undefined") {
    return resolvedTheme;
  }
  if (suppressTransitions) {
    document.documentElement.classList.add("no-transitions");
  }

  document.documentElement.classList.toggle("dark", resolvedTheme === "dark");
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = resolvedTheme;
  syncBrowserChromeTheme(resolvedTheme);
  syncDesktopTheme(theme);

  if (suppressTransitions) {
    // Force a reflow so the no-transitions class takes effect before removal.
    // oxlint-disable-next-line no-unused-expressions
    document.documentElement.offsetHeight;
    requestAnimationFrame(() => {
      document.documentElement.classList.remove("no-transitions");
    });
  }

  return resolvedTheme;
}

function applyUiFontSize(uiFontSize: number): void {
  if (typeof document === "undefined") {
    return;
  }
  document.documentElement.style.setProperty(
    "--forge-ui-font-size",
    `${normalizeFontSize(uiFontSize, DEFAULT_UI_FONT_SIZE)}px`,
  );
}

function applyCodeFontSize(codeFontSize: number): void {
  if (typeof document === "undefined") {
    return;
  }
  document.documentElement.style.setProperty(
    "--forge-code-font-size",
    `${normalizeFontSize(codeFontSize, DEFAULT_CODE_FONT_SIZE)}px`,
  );
}

function applyFontSizes(input: {
  readonly uiFontSize: number;
  readonly codeFontSize: number;
}): void {
  applyUiFontSize(input.uiFontSize);
  applyCodeFontSize(input.codeFontSize);
}

if (typeof document !== "undefined") {
  const initialAppearance = readInitialStoredAppearance();
  applyTheme(initialAppearance.theme);
  applyFontSizes(initialAppearance);
}

export function useTheme() {
  const theme = useSettings((settings) => settings.theme);
  const themeId = useSettings((settings) => settings.themeId);
  const uiFontSize = useSettings((settings) => settings.uiFontSize);
  const codeFontSize = useSettings((settings) => settings.codeFontSize);
  const { updateSettings } = useUpdateSettings();
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(theme));
  const { themeHalves, setThemeHalf, clearThemeHalves } = useThemeHalves(themeId, resolvedTheme);

  const setTheme = useCallback(
    (nextTheme: Theme) => {
      updateSettings({ theme: nextTheme });
      setResolvedTheme(applyTheme(nextTheme, true));
    },
    [updateSettings],
  );

  const setThemeId = useCallback(
    (nextThemeId: string) => {
      updateSettings({ themeId: nextThemeId });
    },
    [updateSettings],
  );

  const setUiFontSize = useCallback(
    (nextFontSize: number) => {
      const normalizedFontSize = normalizeFontSize(nextFontSize, DEFAULT_UI_FONT_SIZE);
      updateSettings({ uiFontSize: normalizedFontSize });
      applyUiFontSize(normalizedFontSize);
    },
    [updateSettings],
  );

  const setCodeFontSize = useCallback(
    (nextFontSize: number) => {
      const normalizedFontSize = normalizeFontSize(nextFontSize, DEFAULT_CODE_FONT_SIZE);
      updateSettings({ codeFontSize: normalizedFontSize });
      applyCodeFontSize(normalizedFontSize);
    },
    [updateSettings],
  );

  useEffect(() => {
    setResolvedTheme(applyTheme(theme));
  }, [theme]);

  useEffect(() => {
    applyFontSizes({ uiFontSize, codeFontSize });
  }, [uiFontSize, codeFontSize]);

  useEffect(() => {
    if (theme !== "system" || typeof window === "undefined") {
      return;
    }
    if (typeof window.matchMedia !== "function") {
      return;
    }

    const mediaQuery = window.matchMedia(SYSTEM_DARK_QUERY);
    const handleSystemThemeChange = () => {
      setResolvedTheme(applyTheme("system", true));
    };
    mediaQuery.addEventListener("change", handleSystemThemeChange);
    return () => {
      mediaQuery.removeEventListener("change", handleSystemThemeChange);
    };
  }, [theme]);

  return {
    theme,
    setTheme,
    themeId,
    setThemeId,
    resolvedTheme,
    themeHalves,
    setThemeHalf,
    clearThemeHalves,
    uiFontSize,
    codeFontSize,
    setUiFontSize,
    setCodeFontSize,
  };
}

/**
 * Applies the per-surface appearance preferences whenever they change.
 *
 * Separate from `useTheme` because these do not need the pre-paint path: the
 * interface size and the palette decide the *shape and colour* of first paint
 * and would flash if they arrived late, whereas a code font or a glass opacity
 * settles a frame in without anyone noticing. Keeping them apart is what lets
 * the pre-paint read stay a small, schema-free parse of two numbers rather
 * than growing into a second copy of the settings decoder.
 *
 * Mounted once, at the app root. `applyAppearanceSurfaces` is idempotent, so a
 * second mount would be harmless rather than wrong — but it would also be a
 * second source of truth for the same properties, which is how they drift.
 */
export function useAppearanceSurfaces(): void {
  const appearanceContrast = useSettings((settings) => settings.appearanceContrast);
  const fontFamilySans = useSettings((settings) => settings.fontFamilySans);
  const fontFamilyCode = useSettings((settings) => settings.fontFamilyCode);
  const fontFamilyComposer = useSettings((settings) => settings.fontFamilyComposer);
  const fontFamilyTerminal = useSettings((settings) => settings.fontFamilyTerminal);
  const promptFontSize = useSettings((settings) => settings.promptFontSize);
  const terminalFontSize = useSettings((settings) => settings.terminalFontSize);
  const fontSmoothing = useSettings((settings) => settings.fontSmoothing);
  const glassOpacity = useSettings((settings) => settings.glassOpacity);

  useEffect(() => {
    if (typeof document === "undefined") return;
    applyAppearanceSurfaces(document.documentElement, {
      appearanceContrast,
      fontFamilyCode,
      fontFamilyComposer,
      fontFamilySans,
      fontFamilyTerminal,
      fontSmoothing,
      glassOpacity,
      promptFontSize,
      terminalFontSize,
    });
  }, [
    appearanceContrast,
    fontFamilyCode,
    fontFamilyComposer,
    fontFamilySans,
    fontFamilyTerminal,
    fontSmoothing,
    glassOpacity,
    promptFontSize,
    terminalFontSize,
  ]);
}
